import { useRef, useEffect } from "react";
import UndoRedoStack, { Action } from "./undo-redo-stack";
import useObservableController from "elements/hooks/use-observable-controller";
import { M } from "shared/datamodel/mutators";
import { Reflect } from "@workcanvas/reflect/client";
import { CanvasElement } from "shared/datamodel/schemas/canvas-element";
import { boundsRefreshOperation } from "frontend/canvas-designer-new";

interface UndoRedoState {
  canUndo: boolean;
  canRedo: boolean;
}

export interface UseUndoRedoResult {
  stack: UndoRedoStack;
  state: UndoRedoState;
  addAction: (action: Action) => void;
  undo: () => void;
  redo: () => void;
  startSequence: () => void;
  endSequence: () => void;
  patchElement: <T extends CanvasElement>(id: string, mutate: (element: T) => void) => Promise<T | undefined>;
  patchElements: <T extends CanvasElement>(
    ids: string[],
    mutate: (element: T) => void,
    sequenceId?: string
  ) => Promise<Record<string, T> | undefined>;
}

export function useUndoRedo(
  reflect: Reflect<M>,
  mutationCallback: () => void,
  needToRefreshBoundsRef: React.MutableRefObject<string | null>
): UseUndoRedoResult {
  const stackRef = useRef<UndoRedoStack>(new UndoRedoStack(reflect));
  useObservableController(stackRef.current);

  useEffect(() => {
    if (reflect) {
      stackRef.current.setReflect(reflect);
    }
  }, [reflect]);

  const canUndo = stackRef.current.canUndo();
  const canRedo = stackRef.current.canRedo();

  const addAction = (action: Action) => {
    stackRef.current.addAction(action);
    mutationCallback();
  };

  const undo = () => {
    stackRef.current.undo();
    mutationCallback();
    if (needToRefreshBoundsRef) {
      needToRefreshBoundsRef.current = boundsRefreshOperation.undo;
    }
  };

  const redo = () => {
    stackRef.current.redo();
    mutationCallback();
    if (needToRefreshBoundsRef) {
      needToRefreshBoundsRef.current = boundsRefreshOperation.redo;
    }
  };

  return {
    stack: stackRef.current,
    state: {
      canUndo,
      canRedo,
    },
    addAction,
    undo,
    redo,
    startSequence: () => stackRef.current.startSequence(),
    endSequence: () => stackRef.current.endSequence(),
    patchElement: (id, mutate) => stackRef.current.patchElement(id, mutate),
    patchElements: (ids, mutate, sequenceId) => stackRef.current.patchElements(ids, mutate, sequenceId),
  };
}
